// ════════════════════════════════════════════════════════════════════
// Glass-i — Customer Price Lists: pieces shared by the register and the
// card page. Split out when the card moved from a Drawer to its own route
// (/erp/masters/price-lists/:id) so both sides keep one status vocabulary,
// one pager and one set of formatters.
// ════════════════════════════════════════════════════════════════════

import { Tooltip } from 'antd';
import dayjs from 'dayjs';

export const DATE_FMT = 'DD-MM-YYYY';
export const API_DATE = 'YYYY-MM-DD';

// The five price sources a list can override, in the order the tabs appear.
export const KINDS = [
  { key: 'ITEM', label: 'Glass & Items', unitHint: 'SQM / SQF' },
  { key: 'FABRICATION', label: 'Fabrication', unitHint: 'per hole / pc' },
  { key: 'PROCESS', label: 'Processes', unitHint: 'RFT / SQF' },
  { key: 'OTHER_CHARGE', label: 'Other Charges', unitHint: 'INR / %' },
  { key: 'MANUAL_CHARGE', label: 'Manual Charges', unitHint: 'INR' }
];

// Status is the single most important fact about a card — whether it is
// pricing real documents right now — so it drives colour everywhere it
// appears: the register pill, the drawer's accent band and its dot.
//
// `accent` values are the ConfigProvider tokens from main.jsx (colorPrimary /
// colorSuccess), not new colours; `soft`/`ring` are the tints of those, used
// only as one-off surface washes, which is what Tailwind/inline colour is for
// (docs/23). Nothing here overrides a theme token.
//
// ⚠️ DRAFT and ARCHIVED were BOTH `default` before, so a card that is not live
// yet looked identical to one that is dead — opposite meanings, same grey pill.
// Draft is "in progress" (docs/23's palette → `processing`); archived is the
// genuinely inert one that keeps grey.
export const STATUS_TAG = {
  DRAFT: {
    color: 'processing', text: 'Draft',
    accent: '#1877F2', soft: '#F0F6FF', ring: '#BFDBFE',
    hint: 'Not live yet — it prices nothing until you activate it.'
  },
  ACTIVE: {
    color: 'green', text: 'Active',
    accent: '#16A34A', soft: '#F0FDF4', ring: '#BBF7D0',
    hint: 'Live — it prices new documents for the customers assigned to it.'
  },
  ARCHIVED: {
    color: 'default', text: 'Archived',
    accent: '#64748B', soft: '#F8FAFC', ring: '#E2E8F0',
    hint: 'Retired — it prices nothing and can no longer be edited. Duplicate it to start a new card from these rates.'
  }
};
export const statusTheme = (s) => STATUS_TAG[s] || STATUS_TAG.ARCHIVED;

/**
 * A compact figure in the drawer's identity band. Deliberately NOT an AntD
 * `Statistic`: that renders at hero scale for a dashboard tile, and these sit
 * inside a 40px band beside a sentence. Same role, right size.
 */
export function Fact({ label, value, tone, hint }) {
  const body = (
    <div style={{ minWidth: 64 }}>
      <div style={{
        fontSize: 9.5, textTransform: 'uppercase', letterSpacing: '.06em',
        color: '#64748b', fontWeight: 700, lineHeight: 1.4
      }}>{label}</div>
      <div style={{
        fontSize: 15, fontWeight: 700, lineHeight: 1.25,
        color: tone || '#0f172a', fontVariantNumeric: 'tabular-nums'
      }}>{value}</div>
    </div>
  );
  return hint ? <Tooltip title={hint}>{body}</Tooltip> : body;
}

/** The status dot that precedes the code in the drawer title. */
export function StatusDot({ status }) {
  const t = statusTheme(status);
  return (
    <span
      aria-hidden
      style={{
        display: 'inline-block', width: 9, height: 9, borderRadius: '50%',
        background: t.accent, boxShadow: `0 0 0 3px ${t.ring}`, flex: '0 0 auto'
      }}
    />
  );
}

export const money = (v) => (v === null || v === undefined || v === ''
  ? '—'
  : Number(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));

export const toApiDate = (d) => (d ? dayjs(d).format(API_DATE) : null);
export const fromApiDate = (s) => (s ? dayjs(s, API_DATE) : null);

/** A list whose end date has passed prices nothing — derived, never stored. */
export function isExpired(row) {
  return !!(row && row.effectiveTo && dayjs(row.effectiveTo, API_DATE).isBefore(dayjs(), 'day'));
}

// Pagination shared by the tables in this module, so the register, the
// assignments tab and the history tab all behave the same way. `showTotal` is
// the part that earns its place: "1-10 of 23" answers "is that all of them?",
// which is the question an operator actually has when a list is truncated.
export const pager = (pageSize, extra = {}) => ({
  pageSize,
  size: 'small',
  showSizeChanger: true,
  pageSizeOptions: [10, 25, 50, 100],
  showTotal: (t, [a, z]) => `${a}-${z} of ${t}`,
  ...extra
});

export function errMessage(e, fallback) {
  return (e && e.response && e.response.data && e.response.data.error
    && e.response.data.error.message) || (e && e.message) || fallback;
}

// ── The register grid ───────────────────────────────────────────────
//
// The grid itself wears the HOUSE classes `erp-grid erp-grid--compact`
// (index.css) — the same ones ErpDataGrid applies and the /invoices and /users
// grids use. Those already supply the slate header band, the hairline borders,
// the column separators, tabular figures and the Excel-like 32px row, so the
// register now matches every other ERP grid instead of inventing a second look.
//
// What is left here is only what the house class has no opinion about: the
// toolbar that sits above this particular table, and the pointer affordance for
// a row that opens a card. Two rules, not forty.
export const GRID_CSS = `
.pl-grid .ant-table-tbody > tr.pl-row{cursor:pointer;}
.pl-toolbar{display:flex;gap:10px;flex-wrap:wrap;align-items:center;
  padding:0 0 10px;border-bottom:1px solid #F1F5F9;margin-bottom:2px;}
`;

export const PAGE_CSS = `
.pl-page{display:flex;flex-direction:column;min-height:0;}
/* Sticky so Save stays reachable from the bottom of a 200-row rate grid —
   the one thing the Drawer did better than a page, kept. */
.pl-actionbar{position:sticky;top:0;z-index:20;display:flex;align-items:center;gap:12px;flex-wrap:wrap;
  background:#fff;border:1px solid #E2E8F0;border-radius:14px;padding:10px 14px;margin-bottom:14px;
  box-shadow:0 1px 2px rgba(15,23,42,.04);}
.pl-body{min-height:0;}
.pl-activate.ant-btn{background:#16A34A;border-color:#16A34A;color:#fff;}
.pl-activate.ant-btn:not(:disabled):hover{background:#15803D!important;border-color:#15803D!important;color:#fff!important;}
.pl-activate.ant-btn:not(:disabled):active{background:#166534!important;border-color:#166534!important;}
.pl-activate.ant-btn .anticon{color:#fff;}
`;

